import axios, { AxiosError } from 'axios';
import { isAllowedChatMediaUrl } from '../../components/Chatbot/chatMediaUrl';
import { UserRoles } from '../../shared/constants';
import { mapPermissionsToApiRoles } from '../chatbot/chatbotRoles';
import { documentConfig } from './documentConfig';
import { documentDebugError, documentDebugLog } from './documentDebug';
import {
  DocumentDeleteResponse,
  DocumentHealthResponse,
  DocumentPresignedUpload,
  DocumentStatusResponse,
  DocumentUploadBatchResult,
  DocumentUploadFailure,
  DocumentUploadResult,
} from './documentTypes';

const documentApiClient = axios.create();

const LOG_PREFIX = '[DocumentAPI]';

documentDebugLog(LOG_PREFIX, 'Configured URLs:', documentConfig);

const assertDocumentApiConfigured = (url: string, label: string): void => {
  if (!url) {
    throw new Error(
      `${label} is not configured. Set REACT_APP_CHATBOT_API_HOST in your environment.`,
    );
  }
};

const maskToken = (token: string): string =>
  token ? `${token.slice(0, 8)}...${token.slice(-4)}` : '(none)';

const logRequest = (
  method: string,
  url: string,
  details?: Record<string, unknown>,
): void => {
  documentDebugLog(LOG_PREFIX, `→ ${method} ${url}`, details ?? '');
};

const logResponse = (
  method: string,
  url: string,
  status: number,
  data: unknown,
): void => {
  documentDebugLog(LOG_PREFIX, `← ${method} ${url} [${status}]`, data);
};

const logError = (method: string, url: string, error: unknown): void => {
  const axiosError = error as AxiosError<{ detail?: string }>;
  documentDebugError(LOG_PREFIX, `✗ ${method} ${url}`, {
    status: axiosError.response?.status,
    detail: axiosError.response?.data?.detail,
    message: axiosError.message,
  });
};

interface DocumentPayload {
  file: File;
  permissions: UserRoles[];
}

export interface DocumentRequestContext {
  token: string;
  customerId: number;
}

const getErrorMessage = (error: unknown, fallback: string): string => {
  const axiosError = error as AxiosError<{ detail?: string }>;
  return axiosError.response?.data?.detail ?? fallback;
};

export const buildDocumentAuthHeaders = ({
  token,
  customerId,
}: DocumentRequestContext) => ({
  Authorization: `Bearer ${token}`,
  'customer-id': String(customerId),
});

const getContext = (
  token: string,
  customerId: number,
): DocumentRequestContext => {
  if (!token) {
    throw new Error('Missing access token. Please sign in again.');
  }

  if (!customerId) {
    throw new Error('Missing customer ID for document request.');
  }

  return { token, customerId };
};

const resolveAllowedRolesFromPresign = (
  presign: DocumentPresignedUpload,
  permissions: UserRoles[],
): string => {
  const headers = presign.headers ?? {};
  const fromHeader =
    headers['x-amz-meta-allowed_roles'] ||
    headers['X-Amz-Meta-Allowed_Roles'] ||
    Object.entries(headers).find(
      ([key]) => key.toLowerCase() === 'x-amz-meta-allowed_roles',
    )?.[1];

  if (typeof fromHeader === 'string' && fromHeader.trim()) {
    return fromHeader.trim();
  }

  return mapPermissionsToApiRoles(permissions).join(',');
};

export const normalizePresignFilename = (filename: string): string =>
  filename.trim().toLowerCase().replace(/\s+/g, '_');

export const presignMatchesFilename = (
  presign: DocumentPresignedUpload,
  filename: string,
): boolean => {
  const clientFilename = presign.client_filename?.trim();
  if (clientFilename) {
    return (
      clientFilename === filename ||
      normalizePresignFilename(clientFilename) ===
        normalizePresignFilename(filename)
    );
  }

  const serverFilename = presign.filename?.trim() ?? '';
  return (
    serverFilename === filename ||
    normalizePresignFilename(serverFilename) ===
      normalizePresignFilename(filename)
  );
};


export const findPresignForFile = (
  presigns: DocumentPresignedUpload[],
  filename: string,
  usedIds: Set<string>,
): DocumentPresignedUpload | undefined =>
  presigns.find(
    (item) =>
      !usedIds.has(item.document_id) && presignMatchesFilename(item, filename),
  );

class DocumentService {
  async getHealth(
    token: string,
    customerId: number,
  ): Promise<DocumentHealthResponse> {
    const context = getContext(token, customerId);
    const url = documentConfig.HEALTH_API_URL;
    assertDocumentApiConfigured(url, 'Document health API');
    logRequest('GET', url, {
      customerId,
      token: maskToken(token),
    });

    try {
      const response = await documentApiClient.get<DocumentHealthResponse>(
        url,
        {
          headers: buildDocumentAuthHeaders(context),
        },
      );
      logResponse('GET', url, response.status, response.data);
      return response.data;
    } catch (error) {
      logError('GET', url, error);
      throw error;
    }
  }


  async uploadDocuments(
    documents: DocumentPayload[],
    token: string,
    customerId: number,
  ): Promise<DocumentUploadBatchResult> {
    const context = getContext(token, customerId);

    if (documents.length === 0) {
      throw new Error('Add at least one document before uploading.');
    }

    const missingPermissions = documents.filter(
      (document) => document.permissions.length === 0,
    );
    if (missingPermissions.length > 0) {
      throw new Error(
        `Assign access permissions for: ${missingPermissions
          .map((document) => document.file.name)
          .join(', ')}`,
      );
    }

    const fileRoleMap = documents.reduce<Record<string, string[]>>(
      (accumulator, document) => {
        accumulator[document.file.name] = mapPermissionsToApiRoles(
          document.permissions,
        );
        return accumulator;
      },
      {},
    );

    const formBody = new URLSearchParams();
    formBody.append('allowed_roles', JSON.stringify(fileRoleMap));
    documents.forEach((document) => {
      formBody.append('files', document.file.name);
    });

    const url = documentConfig.UPLOAD_API_URL;
    assertDocumentApiConfigured(url, 'Document upload API');
    logRequest('POST', url, {
      customerId,
      token: maskToken(token),
      fileCount: documents.length,
      filenames: documents.map((document) => document.file.name),
      allowed_roles: fileRoleMap,
    });

    let presigns: DocumentPresignedUpload[];
    try {
      const response = await documentApiClient.post<DocumentPresignedUpload[]>(
        url,
        formBody,
        {
          headers: {
            ...buildDocumentAuthHeaders(context),
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        },
      );

      logResponse('POST', url, response.status, response.data);
      presigns = Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      logError('POST', url, error);
      throw new Error(getErrorMessage(error, 'Failed to get upload URLs'));
    }

    if (presigns.length === 0) {
      throw new Error('Upload API returned no presigned URLs.');
    }

    const usedDocumentIds = new Set<string>();
    const successfulDocumentIds = new Set<string>();
    const cleanedDocumentIds = new Set<string>();
    const results: DocumentUploadResult[] = [];
    const failures: DocumentUploadFailure[] = [];

    const cleanupRegisteredDocument = async (
      documentId: string,
      filename: string,
    ): Promise<void> => {
      if (!documentId || cleanedDocumentIds.has(documentId)) {
        return;
      }

      try {
        await this.deleteDocument(documentId, token, customerId);
        cleanedDocumentIds.add(documentId);
        documentDebugLog(LOG_PREFIX, 'Cleaned up registered document after PUT failure', {
          documentId,
          filename,
        });
      } catch (cleanupError) {
        documentDebugError(
          LOG_PREFIX,
          `Failed to clean up registered document ${documentId} for "${filename}"`,
          cleanupError,
        );
      }
    };

    const recordFailure = async (
      failure: DocumentUploadFailure,
      shouldCleanup: boolean,
    ): Promise<void> => {
      failures.push(failure);
      if (shouldCleanup && failure.document_id) {
        await cleanupRegisteredDocument(failure.document_id, failure.filename);
      }
    };

    for (const document of documents) {
      const filename = document.file.name;
      const presign = findPresignForFile(presigns, filename, usedDocumentIds);

      if (!presign?.presigned_url) {
        await recordFailure(
          {
            filename,
            error: `No matching presigned URL for "${filename}".`,
          },
          false,
        );
        continue;
      }

      usedDocumentIds.add(presign.document_id);

      if (!isAllowedChatMediaUrl(presign.presigned_url)) {
        documentDebugError(
          LOG_PREFIX,
          'Blocked untrusted document upload presigned URL',
          {
            documentId: presign.document_id,
            filename,
            host: (() => {
              try {
                return new URL(presign.presigned_url).hostname;
              } catch {
                return '(invalid)';
              }
            })(),
          },
        );
        await recordFailure(
          {
            filename,
            document_id: presign.document_id,
            error: `Blocked untrusted upload URL for "${filename}".`,
          },
          true,
        );
        continue;
      }

      const s3Headers = { ...(presign.headers ?? {}) };
      const s3UrlForLog = presign.presigned_url.split('?')[0];
      logRequest('PUT', s3UrlForLog, {
        documentId: presign.document_id,
        filename,
        size: document.file.size,
        headers: s3Headers,
      });

      try {
        const fileBytes = await document.file.arrayBuffer();
        const s3Response = await fetch(presign.presigned_url, {
          method: 'PUT',
          headers: s3Headers,
          body: fileBytes,
        });

        if (!s3Response.ok) {
          const errorBody = await s3Response.text().catch(() => '');
          documentDebugError(LOG_PREFIX, `✗ PUT ${s3UrlForLog}`, {
            status: s3Response.status,
            body: errorBody.slice(0, 500),
          });
          await recordFailure(
            {
              filename,
              document_id: presign.document_id,
              error: `Failed to upload "${filename}" to storage (${s3Response.status})`,
            },
            true,
          );
          continue;
        }

        logResponse('PUT', s3UrlForLog, s3Response.status, {
          etag: s3Response.headers.get('etag'),
        });
      } catch (error) {
        logError('PUT', s3UrlForLog, error);
        await recordFailure(
          {
            filename,
            document_id: presign.document_id,
            error: getErrorMessage(
              error,
              `Failed to upload "${filename}" to storage`,
            ),
          },
          true,
        );
        continue;
      }

      successfulDocumentIds.add(presign.document_id);
      results.push({
        document_id: presign.document_id,
        filename,
        status: 'loaded',
        allowed_roles: resolveAllowedRolesFromPresign(
          presign,
          document.permissions,
        ),
      });
    }

    for (const presign of presigns) {
      if (
        successfulDocumentIds.has(presign.document_id) ||
        cleanedDocumentIds.has(presign.document_id)
      ) {
        continue;
      }

      await cleanupRegisteredDocument(
        presign.document_id,
        presign.client_filename || presign.filename || '(unused)',
      );
    }

    return { results, failures };
  }

  async getDocumentStatus(
    documentId: string,
    token: string,
    customerId: number,
  ): Promise<DocumentStatusResponse> {
    const context = getContext(token, customerId);
    const url = documentConfig.STATUS_API_URL;
    assertDocumentApiConfigured(url, 'Document status API');
    logRequest('GET', url, {
      document_id: documentId,
      customerId,
      token: maskToken(token),
    });

    try {
      const response = await documentApiClient.get<DocumentStatusResponse>(
        url,
        {
          params: { document_id: documentId },
          headers: buildDocumentAuthHeaders(context),
        },
      );
      logResponse('GET', url, response.status, response.data);
      return response.data;
    } catch (error) {
      logError('GET', url, error);
      throw new Error(
        getErrorMessage(error, 'Failed to fetch document status'),
      );
    }
  }

  async getAllDocumentStatuses(
    token: string,
    customerId: number,
  ): Promise<DocumentStatusResponse[]> {
    const context = getContext(token, customerId);
    const url = documentConfig.STATUS_ALL_API_URL;
    assertDocumentApiConfigured(url, 'Document status_all API');
    logRequest('GET', url, {
      customerId,
      token: maskToken(token),
    });

    try {
      const response = await documentApiClient.get<DocumentStatusResponse[]>(
        url,
        {
          headers: buildDocumentAuthHeaders(context),
        },
      );
      logResponse('GET', url, response.status, response.data);
      return response.data;
    } catch (error) {
      logError('GET', url, error);
      throw new Error(
        getErrorMessage(error, 'Failed to fetch document statuses'),
      );
    }
  }

  async deleteDocument(
    documentId: string,
    token: string,
    customerId: number,
  ): Promise<DocumentDeleteResponse> {
    const context = getContext(token, customerId);
    const url = documentConfig.DELETE_API_URL;
    assertDocumentApiConfigured(url, 'Document delete API');
    logRequest('DELETE', url, {
      document_id: documentId,
      customerId,
      token: maskToken(token),
    });

    try {
      const response = await documentApiClient.delete<DocumentDeleteResponse>(
        url,
        {
          params: { document_id: documentId },
          headers: buildDocumentAuthHeaders(context),
        },
      );
      logResponse('DELETE', url, response.status, response.data);
      return response.data;
    } catch (error) {
      logError('DELETE', url, error);
      throw new Error(getErrorMessage(error, 'Delete failed'));
    }
  }
}

export const documentService = new DocumentService();
